import React, { useEffect, useReducer, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// @ts-expect-error - react-google-recaptcha doesn't have types
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../contexts/AuthContext';
import { CONSENTTYPE, REQUESTTYPES } from './constant';
import {
  PageHeader,
  Card,
  SectionHeading,
  Select,
  type SelectOption,
  Input,
  Checkbox,
  DatePicker,
  MessageBar,
} from './ui';
import {
  useCountries,
  useStatesByCountry,
  useCountryCallingInfo,
  useDsrTypeOptions,
  useInaccuracyMapping,
} from '../hooks/usePrivacyQueries';
import { useSaveDataSubjectRequest } from '../hooks/usePrivacyMutations';
import { _orderByKey } from '../utils/helper';
import type { DsrRequestPayload } from '../types/api';

interface State {
  isLoaded: boolean;
  actionName: string;
  successMessage: string | null;
  errorMessage: string | null;
  email: string;
  firstName: string;
  lastName: string;
  requestDetails: string;
  residentType: string | null;
  subjectType: string | null;
  isRunning: boolean;
  subjectOptions: SelectOption[];
  residencyOptions: SelectOption[];
  isReCaptchaVerify: boolean;
  categories: string[];
  categoryOptions: any[];
  selectedCountryId: string | null;
  categoryHeader: string;
  inaccuracyMappingsValues: Record<string, any>;
  mappingsValuesErrors: Record<string, string | null>;
}

type Action =
  | { type: 'SET_FIELD'; field: string; value: any }
  | { type: 'SET_MULTIPLE'; payload: Partial<State> }
  | { type: 'SET_INACCURACY_VALUE'; name: string; value: any }
  | { type: 'SET_INACCURACY_ERROR'; name: string; error: string | null }
  | { type: 'TOGGLE_CATEGORY'; id: string }
  | { type: 'RESET' };

const initialState: State = {
  isLoaded: false,
  actionName: '',
  successMessage: null,
  errorMessage: null,
  email: '',
  firstName: '',
  lastName: '',
  requestDetails: '',
  residentType: null,
  subjectType: null,
  isRunning: false,
  subjectOptions: [],
  residencyOptions: [],
  isReCaptchaVerify: false,
  categories: [],
  categoryOptions: [],
  selectedCountryId: null,
  categoryHeader: '',
  inaccuracyMappingsValues: {},
  mappingsValuesErrors: {},
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_MULTIPLE':
      return { ...state, ...action.payload };
    case 'SET_INACCURACY_VALUE':
      return {
        ...state,
        inaccuracyMappingsValues: {
          ...state.inaccuracyMappingsValues,
          [action.name]: action.value,
        },
      };
    case 'SET_INACCURACY_ERROR':
      return {
        ...state,
        mappingsValuesErrors: {
          ...state.mappingsValuesErrors,
          [action.name]: action.error,
        },
      };
    case 'TOGGLE_CATEGORY': {
      const categories = [...state.categories];
      const index = categories.indexOf(action.id);
      if (index > -1) {
        categories.splice(index, 1);
      } else {
        categories.push(action.id);
      }
      return { ...state, categories };
    }
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export const DataSubjectRequest: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const locationState = location.state as { actionName?: string } | null;
  const formRef = useRef<HTMLFormElement>(null);
  const [state, dispatch] = useReducer(reducer, initialState);

  const { data: countriesData } = useCountries();
  const { data: statesData } = useStatesByCountry(state.selectedCountryId || '', !!state.selectedCountryId);
  const { data: callingCodesData } = useCountryCallingInfo();
  const { data: dsrTypeData } = useDsrTypeOptions();
  const { data: inaccuracyData } = useInaccuracyMapping();
  const saveRequestMutation = useSaveDataSubjectRequest();

  useEffect(() => {
    if (!locationState?.actionName) {
      navigate('/', { replace: true });
      return;
    }
    const actionName = locationState.actionName;
    const email = user?.email || '';
    const residentType = user?.residentType || null;
    const subjectType = user?.subjectType || null;
    dispatch({
      type: 'SET_MULTIPLE',
      payload: { actionName, email, residentType, subjectType, isLoaded: true },
    });
  }, [locationState, user, navigate]);

  useEffect(() => {
    if (dsrTypeData) {
      const subjectOptions: SelectOption[] = (dsrTypeData.subjectTypes || [])
        .filter((item: any) => item.isEnable)
        .sort((a: any, b: any) => a.order - b.order)
        .map((item: any) => ({
          key: item.id,
          text: item.name,
        }));
      const residencyOptions: SelectOption[] = (dsrTypeData.residencyTypes || [])
        .filter((item: any) => item.isEnable)
        .sort((a: any, b: any) => a.order - b.order)
        .map((item: any) => ({
          key: item.id,
          text: item.name,
        }));
      const categoryOptions = dsrTypeData.dsrDoNotSellCategory?.categories || [];
      const categoryHeader = dsrTypeData.dsrDoNotSellCategory?.header || '';
      const payload: Partial<State> = { subjectOptions, residencyOptions, categoryOptions, categoryHeader };
      if (!isAuthenticated) {
        if (residencyOptions.length > 0) payload.residentType = residencyOptions[0].key as string;
        if (subjectOptions.length > 0) payload.subjectType = subjectOptions[0].key as string;
      }
      dispatch({ type: 'SET_MULTIPLE', payload });
    }
  }, [dsrTypeData, isAuthenticated]);

  const handleStaticInputFieldsChanges = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    dispatch({ type: 'SET_FIELD', field: target.name, value: target.value });
  };

  const handleDynamicInputFieldsChanges = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    dispatch({ type: 'SET_INACCURACY_VALUE', name: target.name, value: target.value.trim() });
    dispatch({ type: 'SET_INACCURACY_ERROR', name: target.name, error: null });
  };

  const handleSelectChange = (fieldName: string, value: string) => {
    dispatch({ type: 'SET_FIELD', field: fieldName, value });
  };

  const handleCategoryInputChange = (row: any) => {
    dispatch({ type: 'TOGGLE_CATEGORY', id: row.id });
  };

  const handleInaccuracySelectChange = (item: any, value: string) => {
    const name = item.label;
    // Find the option to get its text
    const itemState = getDynamicInaccuracyItemState(item);
    const option = itemState.options.find((o: SelectOption) => String(o.key) === value);
    const text = option?.text || '';
    dispatch({ type: 'SET_INACCURACY_VALUE', name, value: text });
    if (name === 'Location.Country') {
      dispatch({ type: 'SET_FIELD', field: 'selectedCountryId', value });
      dispatch({ type: 'SET_INACCURACY_VALUE', name: 'Location.State', value: '' });
    }
  };

  const handleDateChange = (item: any, date: Date | undefined) => {
    dispatch({ type: 'SET_INACCURACY_VALUE', name: item.label, value: date || null });
  };

  const onChange = (value: string | null) => {
    dispatch({ type: 'SET_FIELD', field: 'isReCaptchaVerify', value: !!value });
  };

  const backToPrivacyCenter = () => {
    navigate('/', { replace: true });
  };

  const saveRequest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!state.isReCaptchaVerify && state.actionName !== CONSENTTYPE) {
      dispatch({ type: 'SET_FIELD', field: 'errorMessage', value: 'Please verify the reCAPTCHA' });
      setTimeout(() => dispatch({ type: 'SET_FIELD', field: 'errorMessage', value: null }), 5000);
      return;
    }

    if (state.actionName === REQUESTTYPES.DONOTSELL && state.categories.length === 0) {
      dispatch({ type: 'SET_FIELD', field: 'errorMessage', value: 'Please select at least one category' });
      setTimeout(() => dispatch({ type: 'SET_FIELD', field: 'errorMessage', value: null }), 5000);
      return;
    }

    if (!state.subjectType) {
      dispatch({ type: 'SET_FIELD', field: 'errorMessage', value: 'Subject type is required' });
      setTimeout(() => dispatch({ type: 'SET_FIELD', field: 'errorMessage', value: null }), 5000);
      return;
    }

    if (!state.residentType) {
      dispatch({ type: 'SET_FIELD', field: 'errorMessage', value: 'Residency type is required' });
      setTimeout(() => dispatch({ type: 'SET_FIELD', field: 'errorMessage', value: null }), 5000);
      return;
    }

    dispatch({ type: 'SET_FIELD', field: 'isRunning', value: true });

    const requestData: DsrRequestPayload = {
      email: state.email,
      firstName: state.firstName,
      lastName: state.lastName,
      requestDetails: state.requestDetails,
      requestType: state.actionName,
      residentType: state.residentType,
      subjectType: state.subjectType,
      categories: state.categories,
      recaptchaToken: '',
      ...state.inaccuracyMappingsValues,
    };

    saveRequestMutation.mutate(requestData, {
      onSuccess: () => {
        dispatch({ type: 'SET_FIELD', field: 'isRunning', value: false });
      },
      onError: (error: any) => {
        dispatch({ type: 'SET_FIELD', field: 'isRunning', value: false });
        dispatch({
          type: 'SET_FIELD',
          field: 'errorMessage',
          value: error?.message || 'Failed to submit request',
        });
        setTimeout(() => dispatch({ type: 'SET_FIELD', field: 'errorMessage', value: null }), 10000);
      },
    });
  };

  const getDynamicInaccuracyItemState = (item: any) => {
    if (item.label === 'Location.Country') {
      return {
        options: (countriesData || []).map((c: any) => ({ key: c.id, text: c.name })),
        loaded: true,
      };
    } else if (item.label === 'Location.State') {
      return {
        options: (statesData || []).map((s: any) => ({ key: s.code, text: s.name })),
        loaded: !!statesData,
      };
    } else if (item.label === 'Country.CallingCode') {
      return {
        options: (callingCodesData || []).map((cc: any) => ({ key: cc.name, text: cc.dialCode })),
        loaded: true,
      };
    }
    return { options: [], loaded: false };
  };

  if (!state.isLoaded) {
    return null;
  }

  const inaccuracyMappings = inaccuracyData?.inaccuracyMappings
    ? _orderByKey(inaccuracyData.inaccuracyMappings, 'order')
    : [];

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title={`${state.actionName} Request`} />

      {/* Messages */}
      {state.successMessage && (
        <div className="mb-4">
          <MessageBar variant="success">{state.successMessage}</MessageBar>
        </div>
      )}
      {state.errorMessage && (
        <div className="mb-4">
          <MessageBar variant="error">{state.errorMessage}</MessageBar>
        </div>
      )}

      <Card className="p-6 sm:p-8">
        <form ref={formRef} id="saverequest" onSubmit={saveRequest}>
          <ReCAPTCHA sitekey="6Ldfkb8UAAAAALS9dngp1jU_E9kC91vbKvYV0UQd" onChange={onChange}>
            {/* Residency & Subject Type */}
            {!isAuthenticated && (state.residencyOptions.length > 0 || state.subjectOptions.length > 0) && (
              <div className="mb-8">
                <SectionHeading className="mb-4">Your Information</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {state.residencyOptions.length > 0 && (
                    <Select
                      label="I reside in"
                      options={state.residencyOptions}
                      placeholder="Select one"
                      value={state.residentType}
                      onChange={(value) => handleSelectChange('residentType', value)}
                      required
                    />
                  )}
                  {state.subjectOptions.length > 0 && (
                    <Select
                      label="I am a (an)"
                      options={state.subjectOptions}
                      placeholder="Select one"
                      value={state.subjectType}
                      onChange={(value) => handleSelectChange('subjectType', value)}
                      required
                    />
                  )}
                </div>
              </div>
            )}

            {/* Do Not Sell Categories */}
            {state.actionName === REQUESTTYPES.DONOTSELL && (
              <div className="mb-8">
                <SectionHeading className="mb-4">
                  {state.categoryHeader} <span className="text-red-500">*</span>
                </SectionHeading>
                <div className="space-y-3">
                  {state.categoryOptions.map((row) => (
                    <Checkbox
                      key={'cbk' + row.id}
                      label={row.name}
                      checked={!!state.categories.find((e) => e === row.id)}
                      name="categories"
                      onCheckedChange={() => handleCategoryInputChange(row)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Contact Details */}
            {!isAuthenticated && (
              <div className="mb-8">
                <SectionHeading className="mb-4">Contact Details</SectionHeading>
                <div className="space-y-4">
                  <Input
                    type="email"
                    name="email"
                    label="Email"
                    value={state.email}
                    onChange={handleStaticInputFieldsChanges}
                    required
                  />
                  {state.actionName === REQUESTTYPES.INACCURACIES &&
                    inaccuracyMappings
                      .filter((item: any) => item.isSelected)
                      .map((item: any) => {
                        const itemState = getDynamicInaccuracyItemState(item);
                        if (item.dataType === 'date' || item.label === 'Person.BirthDate') {
                          return (
                            <DatePicker
                              key={item.id}
                              label={item.displayName}
                              onSelectDate={(date) => handleDateChange(item, date)}
                              value={state.inaccuracyMappingsValues[item.label]}
                              required={item.isRequired}
                            />
                          );
                        } else if (item.dataType === 'dropdown') {
                          return (
                            <Select
                              key={item.id}
                              label={item.displayName}
                              options={itemState.options}
                              value={state.inaccuracyMappingsValues[item.label]}
                              onChange={(value) => handleInaccuracySelectChange(item, value)}
                              required={item.isRequired}
                            />
                          );
                        } else {
                          return (
                            <Input
                              key={item.id}
                              name={item.label}
                              label={item.displayName}
                              value={state.inaccuracyMappingsValues[item.label] || ''}
                              onChange={handleDynamicInputFieldsChanges}
                              errorMessage={state.mappingsValuesErrors[item.label] || undefined}
                              required={item.isRequired}
                            />
                          );
                        }
                      })}
                  {state.actionName !== REQUESTTYPES.INACCURACIES && (
                    <>
                      <Input
                        name="firstName"
                        label="First Name"
                        value={state.firstName}
                        onChange={handleStaticInputFieldsChanges}
                      />
                      <Input
                        name="lastName"
                        label="Last Name"
                        value={state.lastName}
                        onChange={handleStaticInputFieldsChanges}
                      />
                    </>
                  )}
                  <Input
                    name="requestDetails"
                    label="Request Details"
                    multiline
                    rows={4}
                    value={state.requestDetails}
                    onChange={handleStaticInputFieldsChanges}
                  />
                </div>
              </div>
            )}

            {isAuthenticated && (
              <div className="mb-8">
                <SectionHeading className="mb-4">Request Details</SectionHeading>
                <Input
                  name="requestDetails"
                  label="Request Details"
                  multiline
                  rows={4}
                  value={state.requestDetails}
                  onChange={handleStaticInputFieldsChanges}
                />
              </div>
            )}
          </ReCAPTCHA>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 mt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={backToPrivacyCenter}
              disabled={state.isRunning}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={state.isRunning}
              className="bg-primary text-white hover:bg-primary/90 rounded-lg px-6 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {state.isRunning ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  Submit
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};
